import {
  ApiClientError,
  type ApiErrorBody,
  type ApiResponse,
  type PaginatedResponse,
  UNEXPECTED_ERROR_MESSAGE,
} from "../types/api";

let onUnauthorized: (() => void) | null = null;
let unauthorizedPending = false;

export function setOnUnauthorized(handler: () => void): void {
  onUnauthorized = handler;
}

function handleUnauthorized(): void {
  if (unauthorizedPending) return;
  unauthorizedPending = true;
  try {
    onUnauthorized?.();
  } finally {
    setTimeout(() => {
      unauthorizedPending = false;
    }, 1000);
  }
}

interface RequestOptions {
  /** Skip 401 handling (used for auth endpoints). */
  skipUnauthorized?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isUpload?: boolean,
  options?: RequestOptions,
): Promise<T> {
  const headers: Record<string, string> = {};
  let requestBody: BodyInit | undefined;

  if (body !== undefined && !isUpload) {
    headers["Content-Type"] = "application/json";
    requestBody = JSON.stringify(body);
  } else if (body instanceof FormData) {
    requestBody = body;
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: requestBody,
      credentials: "include",
    });
  } catch {
    // A network failure: the browser's own text ("Failed to fetch") is never shown.
    throw new ApiClientError(0, "UNKNOWN_ERROR", UNEXPECTED_ERROR_MESSAGE);
  }

  if (response.status === 401 && !options?.skipUnauthorized) {
    handleUnauthorized();
    throw new ApiClientError(401, "SESSION_EXPIRED", "Session expired");
  }

  if (!response.ok) {
    let code = "UNKNOWN_ERROR";
    let message = UNEXPECTED_ERROR_MESSAGE;
    let details: Record<string, string[]> | undefined;

    // Every refusal the backend sends, the ledger's and the router's own,
    // arrives in ONE envelope, {"error": {"code", "message"}}; its code and
    // sentence reach the caller word for word, because the sentence is the
    // server's constant and the screen never composes its own. A body
    // without that envelope (or no JSON at all) keeps the defaults above.
    try {
      const errorBody = (await response.json()) as Partial<ApiErrorBody>;
      code = errorBody.error?.code ?? code;
      message = errorBody.error?.message ?? message;
      details = errorBody.error?.details;
    } catch {
      // Not JSON: keep the unknown-error defaults.
    }

    throw new ApiClientError(response.status, code, message, details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    // A 200 whose body is not JSON (a proxy or the index page answering an /api path): the
    // parser's message quotes the body, so the one generic sentence is shown instead.
    throw new ApiClientError(response.status, "UNKNOWN_ERROR", UNEXPECTED_ERROR_MESSAGE);
  }
}

/** GET that unwraps { data: T } envelope. */
export async function get<T>(path: string): Promise<T> {
  const response = await request<ApiResponse<T>>("GET", path);
  return response.data;
}

/** GET that returns the response body as-is (no envelope unwrap). */
export async function getRaw<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

export async function getList<T>(
  path: string,
  params?: Record<string, string>,
): Promise<PaginatedResponse<T>> {
  const url = params ? `${path}?${new URLSearchParams(params).toString()}` : path;
  return request<PaginatedResponse<T>>("GET", url);
}

/** POST that unwraps { data: T } envelope. */
export async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await request<ApiResponse<T>>("POST", path, body);
  return response.data;
}

/** POST that returns the response body as-is (no envelope unwrap). */
export async function postRaw<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body);
}

/**
 * POST that skips 401 handling (for auth endpoints like login/signup
 * where a 401 means bad credentials, not session expiry).
 */
export async function postAuth<T>(path: string, body: unknown): Promise<T> {
  return request<T>("POST", path, body, false, { skipUnauthorized: true });
}

/**
 * GET that skips 401 handling (for auth check endpoints where a 401
 * means "not logged in", not session expiry).
 */
export async function getAuth<T>(path: string): Promise<T> {
  return request<T>("GET", path, undefined, false, {
    skipUnauthorized: true,
  });
}

/** PUT that unwraps { data: T } envelope. */
export async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await request<ApiResponse<T>>("PUT", path, body);
  return response.data;
}

/** PATCH that returns the response body as-is (no envelope unwrap). */
export async function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>("PATCH", path, body);
}

/** PATCH that unwraps { data: T } envelope. */
export async function patchWrapped<T>(path: string, body: unknown): Promise<T> {
  const response = await request<ApiResponse<T>>("PATCH", path, body);
  return response.data;
}

export async function del(path: string): Promise<void> {
  await request<void>("DELETE", path);
}

/** Upload (POST with FormData) that unwraps { data: T } envelope. */
export async function upload<T>(path: string, formData: FormData): Promise<T> {
  const response = await request<ApiResponse<T>>("POST", path, formData, true);
  return response.data;
}

/** Upload (POST with FormData) that returns body as-is. */
export async function uploadRaw<T>(path: string, formData: FormData): Promise<T> {
  return request<T>("POST", path, formData, true);
}
